/* Adapter for the Moast version referenced by product-moast-videos.liquid.
 * Slide widths, aspect ratios and media continue to come from the Moast API.
 * _carousel is Moast's Embla instance; guard it so an app update fails softly.
 */
let modulePromise;

class ProductMoastVideos extends HTMLElement {
  async connectedCallback() {
    this.widget = this.querySelector('moast-carousel');
    if (!this.widget) return;
    this.refresh = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.sync();
      });
    };
    this.updateProgress = () => this.renderProgress();
    try {
      if (!customElements.get('moast-carousel')) {
        modulePromise ||= import(this.dataset.module);
        await modulePromise;
      }
      await customElements.whenDefined('moast-carousel');
      await this.widget.updateComplete;
      if (!this.isConnected || !this.widget.shadowRoot) return;

      this.observer = new MutationObserver(this.refresh);
      this.observer.observe(this.widget, { attributes: true });
      this.observer.observe(this.widget.shadowRoot, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class']
      });
      this.sync();
    } catch (error) {
      console.warn('Product videos could not be initialized.', error);
    }
  }

  sync() {
    const widget = this.widget;
    const root = widget.shadowRoot;
    if (!root) return;
    // The API applies admin attributes asynchronously. Apply the design after it.
    const attributes = {
      'pagination-mode': 'overlay',
      'pagination-mode-mobile': 'overlay', 'no-loop': ''
    };
    for (const [name, value] of Object.entries(attributes)) {
      if (widget.getAttribute(name) !== value) widget.setAttribute(name, value);
    }
    if (!root.querySelector('[data-wo-video-style]')) {
      const style = this.querySelector('[data-video-styles]').content.firstElementChild.cloneNode(true);
      style.dataset.woVideoStyle = '';
      root.append(style);
    }
    const wrapper = root.querySelector('.carousel-wrapper.has-media');
    if (!wrapper) return;
    if (!wrapper.querySelector('.wo-video-progress')) {
      this.progress = document.createElement('div');
      this.progress.className = 'wo-video-progress';
      this.progress.setAttribute('role', 'progressbar');
      this.progress.setAttribute('aria-label', 'Video carousel position');
      this.progress.setAttribute('aria-valuemin', '0');
      this.progress.setAttribute('aria-valuemax', '100');
      this.thumb = document.createElement('span');
      this.thumb.className = 'wo-video-progress__thumb';
      this.progress.append(this.thumb);
      wrapper.append(this.progress);
    }
    const engine = widget._carousel;
    if (!engine || typeof engine.scrollProgress !== 'function') return;
    if (this.engine !== engine) {
      this.unbind();
      this.engine = engine;
      for (const event of ['scroll', 'select', 'reInit', 'resize']) {
        engine.on(event, this.updateProgress);
      }
    }
    this.renderProgress();
  }

  renderProgress() {
    if (!this.engine || !this.progress || !this.thumb) return;
    const slides = this.engine.slideNodes();
    const first = slides[0];
    const last = slides[slides.length - 1];
    if (!first || !last) { this.progress.hidden = true; return; }
    const total = last.offsetLeft + last.offsetWidth - first.offsetLeft;
    const viewport = this.engine.rootNode().clientWidth;
    const fraction = total > 0 ? Math.min(1, viewport / total) : 1;
    const position = Math.max(0, Math.min(1, this.engine.scrollProgress()));
    this.progress.hidden = fraction >= 1;
    this.thumb.style.width = `${fraction * 100}%`;
    this.thumb.style.marginLeft = `${position * (1 - fraction) * 100}%`;
    this.progress.setAttribute('aria-valuenow', String(Math.round(position * 100)));
  }

  unbind() {
    if (!this.engine) return;
    for (const event of ['scroll', 'select', 'reInit', 'resize']) {
      this.engine.off(event, this.updateProgress);
    }
    this.engine = null;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.unbind();
  }
}

if (!customElements.get('wo-product-videos')) {
  customElements.define('wo-product-videos', ProductMoastVideos);
}
